import React from 'react';
import { useMemo, useState } from 'react';
import { useToast } from '../hooks/useToast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '@/api';
import { useAuthFlowBlocker } from '@/navigation/useAuthFlowBlocker';
import { AuthPageShell } from '../components/ui/AuthPageShell';
import { AppButton } from '../components/ui/AppButton';
import {
  getPasswordMatchError,
  getPasswordValidationError,
  PASSWORD_MIN_LENGTH,
} from '../utils/fieldValidation';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function ResetPasswordPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  useAuthFlowBlocker(completed, 'Password was reset. Please sign in from the login page.');

  const submit = async () => {
    if (!token) {
      showToast('Missing reset token', 'error');
      return;
    }
    const passwordErr = getPasswordValidationError(newPassword);
    if (passwordErr) {
      showToast(passwordErr, 'error');
      return;
    }
    const matchErr = getPasswordMatchError(newPassword, confirmPassword);
    if (matchErr) {
      showToast(matchErr, 'error');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword({
        token,
        newPassword,
      });
      setCompleted(true);
      showToast('Password reset successful. Please login.', 'success');
      navigate('/login', { replace: true });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to reset password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full min-h-[44px] px-4 py-3 border border-slate-300 rounded-xl bg-white text-slate-900 transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400';

  return (
    <AuthPageShell title="Reset Password" description="Set a new password for your account.">
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">New password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
          />
          <p className="mt-1 text-xs text-slate-500">At least {PASSWORD_MIN_LENGTH} characters.</p>
        </div>
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">
            Confirm new password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
          />
        </div>
        <AppButton
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={loading}
          onClick={() => void submit()}
        >
          {loading ? 'Updating…' : 'Update Password'}
        </AppButton>
        <div className="text-sm">
          <Link
            to="/login"
            className="font-bold text-indigo-600 transition-colors hover:text-indigo-800"
          >
            Back to login
          </Link>
        </div>
      </div>
    </AuthPageShell>
  );
}
